<script lang="ts">
  interface Props {
    id: string;
    label: string;
    value: string;
    type?: "text" | "email" | "password" | "url" | "number" | "search" | "date" | "datetime-local";
    placeholder?: string;
    autocomplete?: AutoFill;
    inputmode?: "text" | "email" | "numeric" | "url" | "search";
    required?: boolean;
    disabled?: boolean;
    error?: string | undefined;
    hint?: string | undefined;
    /** Keep the label for assistive tech only, e.g. inside a grid whose column header names it. */
    hideLabel?: boolean;
    mono?: boolean;
    onblur?: () => void;
    oninput?: (value: string) => void;
  }

  let {
    id,
    label,
    value = $bindable(),
    type = "text",
    placeholder,
    autocomplete,
    inputmode,
    required = false,
    disabled = false,
    error,
    hint,
    hideLabel = false,
    mono = false,
    onblur,
    oninput
  }: Props = $props();

  const errorId = $derived(`${id}-error`);
  const hintId = $derived(`${id}-hint`);
  // The hint element only renders when there is no error, so only reference it then.
  const describedBy = $derived(
    [error ? errorId : null, hint && !error ? hintId : null].filter(Boolean).join(" ") || undefined
  );
</script>

<div class="field-wrap">
  <label for={id} class:sr-only={hideLabel}>{label}{#if required}<span class="req" aria-hidden="true"> *</span>{/if}</label>
  <input
    {id}
    {type}
    class="field"
    class:mono
    class:invalid={!!error}
    bind:value
    {placeholder}
    {autocomplete}
    {inputmode}
    {required}
    {disabled}
    aria-invalid={error ? "true" : undefined}
    aria-describedby={describedBy}
    onblur={onblur}
    oninput={(e) => oninput?.((e.currentTarget as HTMLInputElement).value)}
  />
  {#if hint && !error}<p id={hintId} class="hint">{hint}</p>{/if}
  {#if error}<p id={errorId} class="error" role="alert">{error}</p>{/if}
</div>

<style>
  .field-wrap {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  label {
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0;
    color: var(--muted);
  }
  .req {
    color: var(--accent);
  }
  .field {
    width: 100%;
    background: var(--bg-2);
    border: 1px solid var(--line-soft);
    color: var(--text);
    border-radius: var(--radius-btn);
    padding: 12px 14px;
    font-family: inherit;
    font-size: 15px;
    transition: border-color var(--dur-3) var(--ease), box-shadow var(--dur-3) var(--ease);
  }
  .field.mono {
    font-family: var(--font-mono);
  }
  .field::placeholder {
    /* --dim is the site's own #5d6b7c .field::placeholder. */
    color: var(--dim);
  }
  /*
   * Site .field:focus — accent border plus the quiet --ring-soft halo. The halo is
   * decoration, not the focus indicator: app.css's :focus-visible outline stays on
   * top of it for keyboard users, and only the redundant mouse-click ring is
   * suppressed.
   */
  .field:focus {
    border-color: var(--accent);
    box-shadow: var(--ring-soft);
  }
  .field:focus:not(:focus-visible) {
    outline: none;
  }
  .field.invalid {
    border-color: var(--err-line);
  }
  .field:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  .hint {
    font-size: 12.5px;
    color: var(--muted);
    margin: 0;
  }
  .error {
    font-size: 12.5px;
    color: var(--err-text);
    margin: 0;
  }
</style>
